/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Box } from "@mui/material";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import DetailRow from "../components/DetailRow";
import AlarmTable from "../components/AlarmTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  formatPower,
  formatOnOff,
  isValidPowerSignal,
  getSignalLedColor,
} from "../utils";

const MxuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  // Alarms
  const mxuAlarms = parsedAttributes.mxuAlarms;
  const mxuAlarmsMask = parsedAttributes.mxuAlarmsMask;
  const hasAlarms = mxuAlarms !== undefined || mxuAlarmsMask !== undefined;

  // Alarm definitions (MXU has 18 alarm bits)
  const alarmDefinitions: AlarmDefinition[] = [
    { bit: 1, name: "Inconsistent Version" },
    { bit: 2, name: "Over Temperature" },
    { bit: 3, name: "Adjustment Fault" },
    { bit: 4, name: "HW Failure" },
    { bit: 5, name: "Synthesizer Clock" },
    { bit: 6, name: "T Sync Loss (TDD)" },
    { bit: 7, name: "RF Connectivity" },
    { bit: 8, name: "DL Out Pwr Low" },
    { bit: 9, name: "UL In Pwr High" },
    { bit: 10, name: "Service Off" },
    { bit: 11, name: "PA VSWR" },
    { bit: 12, name: "PA Shut Down" },
    { bit: 13, name: "PA Permanent Shut Down" },
    { bit: 14, name: "PA Over Temperature" },
    { bit: 15, name: "PA Over Power" },
    { bit: 16, name: "Power Supply" },
    { bit: 17, name: "Synthesizer DL" },
    { bit: 18, name: "Synthesizer UL" },
  ];

  const serviceStateBand = parsedAttributes.serviceStateBand;
  const ulLimiterBand = parsedAttributes.ulLimiterBand;
  const dlPowerBand = parsedAttributes.dlPowerBand;
  const ulPowerDetectorBand = parsedAttributes.ulPowerDetectorBand;

  const hasBandData =
    serviceStateBand !== undefined ||
    ulLimiterBand !== undefined ||
    dlPowerBand !== undefined ||
    ulPowerDetectorBand !== undefined;

  if (!hasAlarms && !hasBandData) return null;

  // If alarmsOnly mode, only render alarm sections
  if (alarmsOnly) {
    if (!hasAlarms) return null;
    
    return (
      <>
        {hasAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={mxuAlarms}
                alarmsMask={mxuAlarmsMask}
                alarmDefinitions={alarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  // Normal mode: show all sections except alarms
  if (!hasBandData) return null;

  return (
    <>
    <Card className="mb-2">
      <CardHeader className="pb-3 px-4 pt-4">
        <CardTitle className="text-sm">RF Information</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {hasBandData && (
          <>
            <DetailRow label="Service State" value={formatOnOff(serviceStateBand)} />
            <DetailRow label="UL Limiter" value={formatOnOff(ulLimiterBand)} />
            {dlPowerBand !== undefined && (
              <div className="flex gap-2 mb-2">
                <span className="text-sm text-muted-foreground font-medium w-2/5 shrink-0">DL Power</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(dlPowerBand)) }} />
                  <span className="text-sm text-foreground">{formatPower(dlPowerBand)}</span>
                </div>
              </div>
            )}
            {ulPowerDetectorBand !== undefined && (
              <div className="flex gap-2 mb-2">
                <span className="text-sm text-muted-foreground font-medium w-2/5 shrink-0">UL Power Detector</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getSignalLedColor(isValidPowerSignal(ulPowerDetectorBand)) }} />
                  <span className="text-sm text-foreground">{formatPower(ulPowerDetectorBand)}</span>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>

    </>
  );
};

export default MxuDeviceDetails;
